/**
 * Reveal Progress
 * Shows the reveal-stage tracker (Corners → Diamond → Center), replacing
 * the small floating phase pill that used to overlap the grid. Lives below
 * the matrix in the middle panel. The pot doesn't get its own line here —
 * it's a Hi/Lo split (half to the rank-axis winner, half to the jewel-axis
 * winner), so each half is shown inline in its matching Best Hand/Best
 * Color box instead of a single combined figure.
 */

import React from 'react';

interface RevealProgressProps {
  phase: string;
}

type StageStatus = 'done' | 'active' | 'pending';

const STAGES: { key: string; label: string }[] = [
  { key: 'corners', label: 'Corners' },
  { key: 'diamond', label: 'Diamond' },
  { key: 'center', label: 'Center' },
];

/** Which stage is currently active, given the game phase. */
function getStageStatuses(phase: string): StageStatus[] {
  // Index: 0 = corners, 1 = diamond, 2 = center
  switch (phase) {
    case 'ROUND_1':
      return ['active', 'pending', 'pending'];
    case 'ROUND_2':
      return ['done', 'active', 'pending'];
    case 'ROUND_3':
    case 'SHOWDOWN':
    case 'ENDED':
      return ['done', 'done', 'active'];
    default:
      // ANTE / PRE_FLOP — nothing revealed yet
      return ['pending', 'pending', 'pending'];
  }
}

const STATUS_STYLE: Record<StageStatus, string> = {
  done: 'bg-green-600 text-white border-green-500',
  active: 'bg-purple-600 text-white border-purple-400 animate-pulse',
  pending: 'bg-gray-800 text-gray-500 border-gray-700',
};

const RevealProgress: React.FC<RevealProgressProps> = ({ phase }) => {
  const statuses = getStageStatuses(phase);

  return (
    <div data-testid="reveal-progress" data-phase={phase} className="flex flex-col items-center gap-3 pt-3 short:!pt-1.5">
      {/* Reveal stage tracker */}
      <div className="flex items-center gap-1 sm:gap-1.5 short:!gap-1">
        {STAGES.map((stage, i) => (
          <React.Fragment key={stage.key}>
            <div
              className={`px-1.5 py-0.5 rounded-full border text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide short:!text-[8px] short:!tracking-normal tight:!text-[8px] tight:!tracking-normal ${STATUS_STYLE[statuses[i]]}`}
            >
              {stage.label}
            </div>
            {i < STAGES.length - 1 && (
              <div className={`w-2 sm:w-3 h-px short:!hidden tight:!hidden ${statuses[i] === 'done' ? 'bg-green-600' : 'bg-gray-700'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default RevealProgress;
